import { Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogField } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePlanSurface } from "@/features/access/use-plan-access";
import type { Baseline } from "@/engine/types";
import { formatDate } from "@/lib/date-format";
import {
  BASELINE_MAX_COUNT,
  BASELINE_NAME_MAX,
  BASELINE_NOTE_MAX,
  displayBaselineName,
  limitText,
} from "@/lib/format";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  cap: {
    color: tokens.colorNeutralForeground3,
  },
  frame: {
    maxHeight: "320px",
    width: "100%",
  },
  fixed: {
    tableLayout: "fixed",
  },
  row: {
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  cell: {
    height: "auto",
    minHeight: "34px",
  },
  pick: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    minWidth: 0,
    margin: 0,
    padding: "2px 0",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  radio: {
    width: "14px",
    height: "14px",
    margin: 0,
    flexShrink: 0,
    accentColor: tokens.colorCompoundBrandBackground,
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
  },
  text: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    flex: 1,
  },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  note: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  when: {
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  center: {
    textAlign: "center",
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  popover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
});

function LimitedInput({
  label,
  value,
  max,
  onValue,
  placeholder,
  disabled,
  hint,
}: {
  label: string;
  value: string;
  max: number;
  onValue: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <DialogField label={label} hint={hint}>
      <Input
        aria-label={label}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          const next = e.target.value;
          const composing = e.nativeEvent instanceof InputEvent && e.nativeEvent.isComposing;
          onValue(composing ? next : limitText(next, max));
        }}
        onCompositionEnd={(e) => onValue(limitText(e.currentTarget.value, max))}
      />
    </DialogField>
  );
}

function saveCapHint(full: boolean, canManage: boolean): string {
  if (!full) return `기준선은 ${BASELINE_MAX_COUNT}개까지 저장합니다.`;
  if (canManage) return `${BASELINE_MAX_COUNT}개까지 찼습니다. 기준선 관리에서 지운 뒤 저장하세요.`;
  return `${BASELINE_MAX_COUNT}개까지 찼습니다.`;
}

function deleteImpact(active: boolean, othersRemain: boolean): string {
  if (!active) return "간트와 리포트의 비교 목록에서 빠집니다.";
  if (othersRemain) return "지금 비교 중입니다. 삭제하면 비교가 마지막으로 저장된 기준선으로 바뀝니다.";
  return "지금 비교 중입니다. 삭제하면 비교할 기준선이 없습니다.";
}

/** 메뉴에서 고르는 한 줄. 긴 이름·시각·메모는 툴팁. */
export function BaselineMenuLabel({ baseline }: { baseline: Baseline }) {
  const name = displayBaselineName(baseline.name);
  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 flex-1 truncate">{name}</span>
          <span className="shrink-0 whitespace-nowrap text-caption tabular-nums text-muted-foreground">
            {formatDate(baseline.createdAt, "short")}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-64 text-left">
        <span className="block">{name}</span>
        <span className="block">{formatDate(baseline.createdAt, "dateTime")}</span>
        {baseline.note ? <span className="block">{baseline.note}</span> : null}
      </TooltipContent>
    </Tooltip>
  );
}

export function BaselineSaveDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const count = useProjectStore((s) => s.project.baselines.length);
  const canManage = usePlanSurface().approveBaseline;
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const full = count >= BASELINE_MAX_COUNT;
  const dismiss = () => {
    setName("");
    setNote("");
    onClose();
  };
  return (
    <AppDialog
      open={open}
      onClose={dismiss}
      title="기준선 저장"
      width="sm"
      footerStart={
        <span className="tabular-nums" aria-label={`저장한 기준선 ${count}개, 최대 ${BASELINE_MAX_COUNT}개`}>
          {count}/{BASELINE_MAX_COUNT}
        </span>
      }
      footer={
        <DialogActionsBar
          onCancel={dismiss}
          confirmLabel="저장"
          disabled={full}
          onConfirm={() => {
            const store = useProjectStore.getState();
            if (store.project.baselines.length >= BASELINE_MAX_COUNT) return;
            store.saveBaseline(name, note);
            dismiss();
          }}
        />
      }
    >
      <LimitedInput
        label="이름"
        value={name}
        max={BASELINE_NAME_MAX}
        onValue={setName}
        disabled={full}
        placeholder={full ? undefined : `기준선 ${count + 1}`}
        hint="지금 일정과 진척이 이후 지연의 기준이 됩니다."
      />
      <LimitedInput
        label="메모"
        value={note}
        max={BASELINE_NOTE_MAX}
        onValue={setNote}
        disabled={full}
        placeholder="요구사항 승인 시점"
        hint={saveCapHint(full, canManage)}
      />
    </AppDialog>
  );
}

type PendingDelete = { id: string; name: string; active: boolean; othersRemain: boolean };

export function BaselineManageDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const baselines = useProjectStore((s) => s.project.baselines);
  const activeId = useProjectStore((s) => s.project.activeBaselineId);
  const [editing, setEditing] = useState<Baseline | null>(null);
  const [pending, setPending] = useState<PendingDelete | null>(null);
  // 메뉴 클릭과 같은 턴에 부모가 닫히면 확인창이 같이 사라진다. 자식이 열리는 순간을 먼저 표시한다.
  const nesting = useRef(false);
  const close = () => {
    if (nesting.current) return;
    setEditing(null);
    setPending(null);
    onClose();
  };
  const openRename = (baseline: Baseline) => {
    nesting.current = true;
    setEditing(baseline);
  };
  const openDelete = (baseline: Baseline) => {
    nesting.current = true;
    setPending({
      id: baseline.id,
      name: displayBaselineName(baseline.name),
      active: activeId === baseline.id,
      othersRemain: baselines.some((item) => item.id !== baseline.id),
    });
  };
  return (
    <AppDialog open={open} onClose={close} title="기준선" width="md" persistWhile={editing !== null || pending !== null}>
      <BaselineManageBody
        baselines={baselines}
        activeId={activeId}
        onArm={() => {
          nesting.current = true;
        }}
        onRename={openRename}
        onDelete={openDelete}
      />
      {editing ? (
        <RenameBaselineDialog
          key={editing.id}
          baseline={editing}
          onClose={() => {
            nesting.current = false;
            setEditing(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={pending !== null}
        title="기준선 삭제"
        message={pending ? "이 기준선을 삭제할까요?" : ""}
        impacts={pending ? [deleteImpact(pending.active, pending.othersRemain)] : undefined}
        confirmLabel="삭제"
        onCancel={() => {
          nesting.current = false;
          setPending(null);
        }}
        onConfirm={() => {
          if (pending) useProjectStore.getState().deleteBaseline(pending.id);
          setPending(null);
        }}
      />
    </AppDialog>
  );
}

function BaselineManageBody({
  baselines,
  activeId,
  onArm,
  onRename,
  onDelete,
}: {
  baselines: Baseline[];
  activeId: string | null;
  onArm: () => void;
  onRename: (baseline: Baseline) => void;
  onDelete: (baseline: Baseline) => void;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const layer = useDialogLayer();
  const canCreate = usePlanSurface().createBaseline;
  const menuProps = { mountNode: layer, positioning: { strategy: "fixed" as const, position: "below" as const, align: "end" as const } };
  if (baselines.length === 0) {
    return (
      <GhostEmptyState
        compact
        title="저장된 기준선이 없습니다"
        description={canCreate ? "기준선 메뉴에서 현재 일정을 저장합니다." : undefined}
      />
    );
  }
  return (
    <>
      {baselines.length >= BASELINE_MAX_COUNT ? (
        <Caption1 className={styles.cap}>{BASELINE_MAX_COUNT}개까지입니다. 지운 뒤 새 기준선을 저장할 수 있습니다.</Caption1>
      ) : null}
      <div className={mergeClasses(table.frame, styles.frame)}>
        <table className={mergeClasses(table.table, styles.fixed)} aria-label="비교 기준선">
          <colgroup>
            <col />
            <col style={{ width: 132 }} />
            <col style={{ width: 56 }} />
          </colgroup>
          <thead>
            <tr>
              <th className={table.th} scope="col">이름</th>
              <th className={mergeClasses(table.th, table.num)} scope="col">저장</th>
              <th className={mergeClasses(table.th, styles.center)} scope="col">동작</th>
            </tr>
          </thead>
          <tbody>
            {baselines.map((baseline) => {
              const name = displayBaselineName(baseline.name);
              const note = baseline.note?.trim() ?? "";
              const active = baseline.id === activeId;
              return (
                <tr key={baseline.id} className={styles.row}>
                  <td className={mergeClasses(table.td, styles.cell)}>
                    <label className={styles.pick}>
                      <input
                        className={styles.radio}
                        type="radio"
                        name="compare-baseline"
                        checked={active}
                        aria-label={note ? `${name}, ${note}, ${formatDate(baseline.createdAt, "dateTime")}` : `${name}, ${formatDate(baseline.createdAt, "dateTime")}`}
                        onChange={() => useProjectStore.getState().setActiveBaseline(baseline.id)}
                      />
                      <Tooltip disableHoverableContent>
                        <TooltipTrigger asChild>
                          <span className={styles.text}>
                            <span className={styles.name}>{name}</span>
                            {note ? <Caption1 className={styles.note}>{note}</Caption1> : null}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent container={layer} side="top" className="max-w-64 text-left">
                          <span className="block">{name}</span>
                          {note ? <span className="block">{note}</span> : null}
                        </TooltipContent>
                      </Tooltip>
                    </label>
                  </td>
                  <td className={mergeClasses(table.td, table.num, styles.when, styles.cell)}>{formatDate(baseline.createdAt, "dateTime")}</td>
                  <td className={mergeClasses(table.td, styles.center, styles.cell)}>
                    <Menu {...menuProps}>
                      <MenuTrigger disableButtonEnhancement>
                        <button type="button" className={styles.menuBtn} aria-label={`${name} 동작`}>
                          <MoreHorizontal16Regular />
                        </button>
                      </MenuTrigger>
                      <MenuPopover className={styles.popover}>
                        <MenuList>
                          <MenuItem onPointerDown={onArm} onClick={() => onRename(baseline)}>이름 바꾸기</MenuItem>
                          <MenuItem onPointerDown={onArm} onClick={() => onDelete(baseline)}>삭제</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RenameBaselineDialog({ baseline, onClose }: { baseline: Baseline; onClose: () => void }) {
  const [name, setName] = useState(() => limitText(baseline.name, BASELINE_NAME_MAX));
  const [note, setNote] = useState(() => limitText(baseline.note ?? "", BASELINE_NOTE_MAX));
  return (
    <AppDialog
      open
      onClose={onClose}
      title="이름 바꾸기"
      width="sm"
      footer={
        <DialogActionsBar
          onCancel={onClose}
          confirmLabel="저장"
          onConfirm={() => {
            useProjectStore.getState().renameBaseline(baseline.id, name, note);
            onClose();
          }}
        />
      }
    >
      <LimitedInput label="이름" value={name} max={BASELINE_NAME_MAX} onValue={setName} />
      <LimitedInput label="메모" value={note} max={BASELINE_NOTE_MAX} onValue={setNote} placeholder="요구사항 승인 시점" />
    </AppDialog>
  );
}
